// JSON client for the inbox endpoints (see InboxMessagesController). The
// create URL is the messages URL — GET reads a conversation, POST sends.

export type InboxUser = {
  id: string
  name: string | null
  username: string | null
}

export type InboxMessageRecord = {
  id: number
  conversation_key: string
  role: "user" | "agent"
  kind: string
  contents: string | null
  sent_by_self: boolean
  other_user: InboxUser | null
  created_at: string
}

function csrfToken(): string | undefined {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

function jsonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-CSRF-Token": csrfToken() ?? "",
  }
}

// One row per conversation — its newest message — newest conversation first.
export async function fetchLatestMessages(latestUrl: string): Promise<InboxMessageRecord[] | null> {
  try {
    const response = await fetch(latestUrl, { headers: { "Accept": "application/json" } })
    if (!response.ok) return null
    const body = (await response.json()) as { latest_messages: InboxMessageRecord[] }
    return body.latest_messages
  } catch {
    return null
  }
}

export async function fetchConversationMessages(
  messagesUrl: string,
  conversationKey: string,
  after: number,
): Promise<{ messages: InboxMessageRecord[]; last_id: number } | null> {
  try {
    const response = await fetch(
      `${messagesUrl}?conversation_key=${encodeURIComponent(conversationKey)}&after=${after}`,
      { headers: { "Accept": "application/json" } },
    )
    if (!response.ok) return null
    return (await response.json()) as { messages: InboxMessageRecord[]; last_id: number }
  } catch {
    return null
  }
}

// Returns the created message, or a string error to show inline. `recipient`
// is a handle the server resolves — a public id today, a username later.
export async function sendInboxMessage(
  messagesUrl: string,
  recipient: string,
  contents: string,
): Promise<InboxMessageRecord | string> {
  if (!csrfToken()) return "Not signed in."

  try {
    const response = await fetch(messagesUrl, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ message: { recipient, contents } }),
    })
    const payload = (await response.json()) as { message?: InboxMessageRecord; error?: string }
    if (!response.ok || !payload.message) return payload.error ?? "Something went wrong."
    return payload.message
  } catch {
    return "Could not reach the server."
  }
}
